import { Ticker } from 'motion-plus/react'
import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import EnvironmentalCertifications
	from '#app/components/marketing/environmental-certifications.tsx'
import {
	EnvironmentSocialGovernance
} from '#app/components/marketing/environmental-social-governance.tsx'
import MarketingImage from '#app/components/marketing/image.tsx'
import { LandingSection } from '#app/components/marketing/landing-section.tsx'
import { ProductLink } from '#app/components/marketing/product-link.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { usePublicContext } from '#app/routes/($lang)+/__public-context.tsx'
import { BASE_URL } from '#app/utils/config.ts'
import { extendedTheme } from '#app/utils/extended-theme.ts'
import { generateMetaTranslationLoader } from '#app/utils/i18next.server.ts'
import { generateTranslatedMeta } from '#app/utils/meta.ts'
import { cn } from '#app/utils/misc.tsx'
import routes from '#routes/index.tsx'
import {
	type Route
} from '../../../../.react-router/types/app/routes/($lang)+/_marketing+/+types/sustainability.ts'

export async function loader({ request }: Route.LoaderArgs) {
	return {
		meta: await generateMetaTranslationLoader({
			request,
			namespace: 'sustainability',
			keys: {
				title: 'hero.title',
				description: 'commitment.description',
				canonical: `${BASE_URL}${routes.public.landings.sustainability.route({})}`,
			},
		}),
	}
}

export const meta = generateTranslatedMeta

export default function ComponentPage() {
	const { t } = useTranslation('sustainability')

	const {
		navigation: { route },
	} = usePublicContext()

	useEffect(() => {
		route.setContent([
			{
				label: t('hero.title'),
			},
		])
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<>
			<LandingSection
				borderToBorderOnX
				className={'text-white'}
				backgroundColor={extendedTheme.colors.brand.dusk.DEFAULT}
			>
				<Text
					as={'h2'}
					size={'pageTitle'}
					type={'serif'}
					weight={'light'}
					className={
						'mx-auto max-w-6xl px-[5vw] text-center lowercase 2xl:max-w-7xl 2xl:px-0'
					}
				>
					<Trans
						t={t}
						i18nKey={'h2Factory.title'}
						components={[<span className="opacity-30" key={0} />]}
					/>
				</Text>

				<div
					className={cn(
						'grid grid-cols-12 gap-x-0 gap-y-xxl md:gap-lg xl:gap-xxl 2xl:gap-xxxl',
						'mt-xl min-[450px]:mt-xxl lg:mt-xxxl',
					)}
				>
					<MarketingImage
						src={'/img/sustainability/factory/1-factory.webp'}
						imageClassName={'object-[center_bottom]'}
						className={cn(
							'max-h-[400px]',
							'col-start-1 col-end-10',
							'row-start-1 row-end-3',
							'md:col-end-7',
							'2xl:max-h-[700px]',
						)}
					/>
					<div
						className={cn(
							'col-start-3',
							'-col-end-2 md:col-start-7',
							'xl:col-end-12',
							'2xl:col-end-10',
						)}
					>
						<Text as={'p'} size={'p'} weight={'light'}>
							{t('h2Factory.description')}
						</Text>
						<Button
							asChild
							variant={'link'}
							className={
								'w-fit items-start px-0 text-start text-body-2xs lowercase text-brand-earth xl:text-body-xs'
							}
						>
							<a
								target={'_blank'}
								rel={'noopener noreferrer'}
								href={'https://www.irisceramicagroup.com/h2-factory/'}
							>
								{t('common:actions.viewMore')}
							</a>
						</Button>
					</div>
					<MarketingImage
						src={'/img/sustainability/factory/2-factory.webp'}
						className={cn(
							'col-start-4 -col-end-1 max-h-[400px]',
							'md:col-start-7 md:row-start-2 md:mt-xxl',
							'2xl:max-h-[700px]',
						)}
					/>

					<div
						className={cn(
							'col-start-2 -col-end-2',
							'md:col-end-10',
							'xl:row-start-3 xl:-mt-xxxl',
							'xl:max-w-3xl 2xl:col-end-7',
						)}
					>
						<div className={'relative flex flex-col gap-lg'}>
							<div
								className={
									'flex flex-col justify-between gap-xl border-l pl-md xl:gap-[7.5vh] xl:pl-lg'
								}
							>
								<div className={'flex items-center gap-xxl'}>
									<Text type={'serif'} as={'h3'} size={'h2'} weight={'light'}>
										{t('h2Factory.quote')}
									</Text>
								</div>
								<MarketingImage
									alt={'Thickness'}
									imageClassName={
										'object-[center_bottom] max-h-[400px] xl:max-h-[50vh]'
									}
									src={'/img/sustainability/factory/3-factory.webp'}
								/>
							</div>
							<ProductLink
								className={cn(
									'self-end',
									'lg:absolute lg:-bottom-xl lg:-right-1/2 lg:-translate-x-1/2',
									'xl:bottom-0',
									'macbook:-right-[60%]',
									'2xl:-right-[120%]',
								)}
								reset
								link={routes.public.landings.fourthDimension.route({})}
								children={t('to.4d.ceramics')
									.split(' ')
									.map((item: string) => (
										<span key={item}>
											{item}
											<br />
										</span>
									))}
							/>
						</div>
					</div>
				</div>
			</LandingSection>

			<LandingSection
				backgroundColor={extendedTheme.colors.brand.dusk.DEFAULT}
				style={{
					backgroundImage: "url('/img/sustainability/4-Ceppo-varese.webp')",
					backgroundSize: 'cover',
					backgroundPosition: '30% center',
				}}
				className={'h-screen text-white'}
				id={'recycled-materials'}
			>
				<Text
					as={'h2'}
					weight={'extralight'}
					size={'pageTitle'}
					type={'serif'}
					className={'-translate-x-[2vw]'}
				>
					<Trans
						t={t}
						i18nKey={'recycled.title'}
						components={[<span className="opacity-50" key={0} />]}
					/>
				</Text>
			</LandingSection>

			<LandingSection
				className={'min-h-[50vh] text-white'}
				backgroundColor={extendedTheme.colors.brand.dusk.DEFAULT}
			>
				<Ticker
					overflow
					gap={200}
					velocity={75}
					items={[
						<Text
							type={'serif'}
							weight={'light'}
							as={'h2'}
							size={'h1'}
							className={'max-w-2xl'}
							key={0}
						>
							{t('manufacturing.title')}
						</Text>,
						<Text
							type={'serif'}
							weight={'light'}
							as={'h2'}
							size={'h1'}
							className={'max-w-2xl'}
							key={1}
						>
							{t('manufacturing.item1')}
						</Text>,
						<Text
							type={'serif'}
							weight={'light'}
							as={'h2'}
							size={'h1'}
							className={'max-w-2xl'}
							key={2}
						>
							{t('manufacturing.item2')}
						</Text>,
						<Text
							type={'serif'}
							weight={'light'}
							as={'h2'}
							size={'h1'}
							className={'max-w-2xl'}
							key={3}
						>
							{t('manufacturing.item3')}
						</Text>,
					]}
				/>
			</LandingSection>

			<EnvironmentalCertifications />

			<EnvironmentSocialGovernance />
		</>
	)
}
